import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { updateLicenseNo, type MyProfileDTO } from '@/api/adapters/my-profile';
import { ApiError } from '@/api/error';
import { MY_PROFILE_QUERY_KEY } from '@/shared/hooks/use-my-profile';
import { toast } from '@/app/store/toast.store';

// SHCT-991 — the staff member enters or changes their own practitioner licence number (decision D16:
// self-edit at any time; an administrator can also correct it). Optional: blank clears it. The hospital
// EMR identifies the doctor by this number, so without it EMR entry fails for that doctor.

const LICENSE_NO_MAX_LENGTH = 20;
// Same set as the server rule and the patient MRN: letters / digits / hyphen, 1–20, case kept.
const LICENSE_NO_REGEX = /^[A-Za-z0-9-]{1,20}$/;
const toInputValue = (raw: string): string =>
  raw.replace(/[^A-Za-z0-9-]/g, '').slice(0, LICENSE_NO_MAX_LENGTH);

interface LicenseNoFieldProps {
  currentLicenseNo: string | null;
}

export function LicenseNoField({ currentLicenseNo }: LicenseNoFieldProps) {
  const { t } = useTranslation('myProfile');
  const queryClient = useQueryClient();
  const saved = currentLicenseNo ?? '';
  const [value, setValue] = useState(saved);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Re-seed when the profile refetches with a different stored value.
  useEffect(() => {
    setValue(saved);
    setError(null);
  }, [saved]);

  const unchanged = value === saved;

  const handleSave = async () => {
    if (saving || unchanged) return;
    if (value && !LICENSE_NO_REGEX.test(value)) {
      setError(t('licenseNo.errors.invalid'));
      return;
    }
    setSaving(true);
    try {
      const { licenseNo } = await updateLicenseNo(value);
      queryClient.setQueryData<MyProfileDTO | undefined>(MY_PROFILE_QUERY_KEY, (prev) =>
        prev ? { ...prev, licenseNo } : prev,
      );
      queryClient.invalidateQueries({ queryKey: MY_PROFILE_QUERY_KEY });
      setError(null);
      toast.success(t('licenseNo.messages.saveSuccess'));
    } catch (err) {
      if (err instanceof ApiError && err.code === 'LICENSE_NO_INVALID') {
        setError(t('licenseNo.errors.invalid'));
      } else if (err instanceof ApiError && err.code === 'LICENSE_NO_DUPLICATE') {
        setError(t('licenseNo.errors.duplicate'));
      } else {
        toast.error(t('licenseNo.errors.generic'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="inline-field">
        <input
          type="text"
          inputMode="numeric"
          aria-label={t('cards.myInfo.fields.licenseNo')}
          className={error ? 'form-field__input--error' : undefined}
          placeholder={t('licenseNo.placeholder')}
          value={value}
          onChange={(e) => {
            setValue(toInputValue(e.target.value));
            setError(null);
          }}
          disabled={saving}
        />
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={handleSave}
          disabled={saving || unchanged}
        >
          {t('licenseNo.actions.save')}
        </button>
      </div>
      {error ? (
        <p className="form-field__error" role="alert">{error}</p>
      ) : (
        <p className="form-field__hint">{t('licenseNo.help')}</p>
      )}
    </>
  );
}
